import { StatChartBaseProps } from '@/components';
import { FontSizeOption, StatChartPropsBase } from '@/core';
import { BasePanelProps, useDataQueryResult } from '../../../plugin-system';
import { StatChartPanelLoaded } from './StatChartPanelLoaded';
import { STAT_CHART_DATA_TYPES, StatChartColorScheme } from './stat-chart-model';

/**
 * Types for the stat chart that are only used in the `Viz` variant.
 *
 * @see {@link StatChartPropsBase} for types defined in the schema because they are also shared with
 * the `DashboardPanel` variant.
 */
interface StatChartPropsVizOnly extends Pick<StatChartBaseProps, 'valueAlign'> {
  /**
   * Whether the text and sparkline colors should be derived from the thresholds or set to the default values.
   * By default, the text is the primary text color and the sparkline is the first color in the categorical palette.
   */
  colorScheme?: StatChartColorScheme;
  /**
   * Optional description text shown below the main number.
   */
  description?: string;
  /**
   * Font size for the description text.
   * If not provided, the description text will use the default font size.
   */
  descriptionFontSize?: FontSizeOption;
}

export interface StatChartPanelProps extends BasePanelProps, StatChartPropsBase, StatChartPropsVizOnly {
  // Extend values from `StatChartPropsBase` below, as needed, to add in jsdoc to help developers.
}

export function StatChartPanel(props: StatChartPanelProps) {
  const { width, height, colorScheme = StatChartColorScheme.DEFAULT } = props;

  const { queryResults } = useDataQueryResult({ dataTypeFilter: STAT_CHART_DATA_TYPES });

  if (queryResults[0]?.error) throw queryResults[0]?.error;

  const contentDimensions = width && height ? { width, height } : undefined;

  if (contentDimensions === undefined) return null;

  return <StatChartPanelLoaded {...props} contentDimensions={contentDimensions} colorScheme={colorScheme} />;
}
